import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AssignDriverRequest, ordersApi } from '../../services/ordersApi';
import { AssignableProvider, transportLabel, TransportType } from '../../types/delivery';
import { customerDisclaimer, INSURANCE_MESSAGES } from '../../utils/insuranceDisclaimer';

/** The provider already frozen onto the leg being reassigned (BR-26). */
export interface CurrentLegProvider {
  providerId: string;
  providerName: string | null;
  /**
   * Spec 072 — the insurance snapshot on the leg. Keeping the current provider keeps THIS (not
   * today's registry row), so the preview and the phone rule read it.
   */
  showDisclaimer?: boolean | null;
  insuredAmount?: number | null;
  disclaimerEn?: string | null;
}

interface AssignDriverModalProps {
  // 'pickup' or 'dropoff' — only changes the wording.
  kind: 'pickup' | 'dropoff';
  onSubmit: (request: AssignDriverRequest) => Promise<void>;
  onClose: () => void;
  /**
   * Reassignment only: the provider snapshotted onto this leg, pre-selected. Keeping it sends no
   * `providerId`, so the existing snapshot — cost and VAT status as they were at assignment —
   * stands (BR-26). Choosing a different one re-snapshots the leg.
   */
  currentProvider?: CurrentLegProvider | null;
  /**
   * Spec 072 v2 — the order's transport type. The list holds only providers of this type, because
   * orders refuses any other with a 422 ("This order needs a Zofeur driver" / "… a recovery truck"),
   * shown in the banner like any other refusal. Absent lists the Recovery registry.
   *
   * #1254 — ZOFEUR has no picker: the provider is always the Zofeur card's own row, shown as a fixed
   * "Zofeur", and no `providerId` is sent (orders assigns that row, re-snapshotting it on a
   * reassignment). Its insurance settings drive the preview and the phone rule.
   */
  transportType?: TransportType;
}

type ProvidersState =
  | { status: 'loading' }
  | { status: 'loaded'; providers: AssignableProvider[] }
  | { status: 'error'; message: string };

/**
 * Spec 053 Flow F3 / BR-26 / BR-27, #633 — assign (or reassign) the driver for one Garagat leg.
 *
 * The recovery provider is REQUIRED and there is no default: the admin names, per leg, which
 * active registry entry performs the trip, so pickup and drop-off can differ. The provider is
 * Garagat's internal cost; it never reaches the driver's WhatsApp or the customer (AC-41b).
 *
 * The list comes from the orders-scoped `/api/admin/orders/recovery-providers` (active providers
 * only), not the Finance registry, so an admin who can assign drivers can always choose one.
 *
 * #1254 — on a Zofeur order the same endpoint answers the Zofeur card's row alone, so the modal shows
 * "Zofeur" with no dropdown (or says the card is not set up yet), plus the driver's name and phone.
 */
export default function AssignDriverModal({ kind, onSubmit, onClose, currentProvider = null, transportType }: AssignDriverModalProps) {
  const auth = useAuth();
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [providerId, setProviderId] = useState(currentProvider?.providerId ?? '');
  const [providers, setProviders] = useState<ProvidersState>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const title = kind === 'pickup' ? 'Assign Pickup Driver' : 'Assign Drop-off Driver';
  const typeLabel = transportLabel(transportType);
  const zofeur = transportType === 'ZOFEUR';

  const loadProviders = useCallback(async () => {
    if (!auth.token) {
      setProviders({ status: 'error', message: 'Not authenticated.' });
      return;
    }
    setProviders({ status: 'loading' });
    try {
      // The endpoint already returns only active providers; sorting here keeps the order stable
      // whatever the server's collation.
      const active = [...await ordersApi.listAssignableProviders(auth.token, transportType)]
        .sort((a, b) => a.name.localeCompare(b.name));
      setProviders({ status: 'loaded', providers: active });
    } catch (err) {
      setProviders({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load recovery providers.' });
    }
  }, [auth.token, transportType]);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const activeProviders = providers.status === 'loaded' ? providers.providers : [];
  // #1254 — the Zofeur card's row: the only provider a Zofeur leg can have, so it is never picked.
  const zofeurRow = zofeur ? activeProviders[0] : undefined;
  // A leg being reassigned may carry a provider that has since been deactivated. It can still be
  // kept (the snapshot stands), but never newly chosen — so it is offered only as "keep current".
  const currentIsInactive = !zofeur && currentProvider !== null
    && providers.status === 'loaded'
    && !activeProviders.some((p) => p.id === currentProvider.providerId);
  const registryEmpty = providers.status === 'loaded' && activeProviders.length === 0 && !currentIsInactive;
  const selectedId = zofeur ? (zofeurRow?.id ?? '') : providerId;

  // Spec 072 — what the customer will read for the chosen provider. Keeping the current provider on a
  // reassignment keeps the leg's snapshot, so that is what is previewed; otherwise the registry row.
  // A Zofeur leg always takes the card's row as it is now, so that is what is previewed.
  const keepingCurrent = !zofeur && currentProvider !== null && providerId === currentProvider.providerId;
  const chosen = zofeur ? zofeurRow : activeProviders.find((p) => p.id === providerId);
  const showsDisclaimer = selectedId === '' ? false
    : keepingCurrent ? !!currentProvider?.showDisclaimer
      : !!chosen?.showInsuranceDisclaimer;
  const preview = selectedId === '' ? null
    : keepingCurrent
      ? customerDisclaimer(currentProvider?.showDisclaimer, currentProvider?.insuredAmount, currentProvider?.disclaimerEn)
      : customerDisclaimer(chosen?.showInsuranceDisclaimer, chosen?.insuredAmount, chosen?.disclaimerEn);
  // The server refuses a blank phone for these providers (422, shown under the field): the driver
  // confirms over WhatsApp. Marked required up front; the server stays the one that refuses.
  const phoneRequired = showsDisclaimer;

  const blockedReason: string | null =
    providers.status === 'loading' ? (zofeur ? 'Loading the Zofeur settings…' : 'Loading recovery providers…')
      : providers.status === 'error' ? (zofeur ? 'The Zofeur settings could not be loaded.' : 'The recovery providers could not be loaded.')
        : registryEmpty && zofeur ? 'Zofeur is not set up yet.'
          : registryEmpty ? `There is no active ${typeLabel ? `${typeLabel} provider` : 'recovery provider'} to assign this trip to.`
            : selectedId === '' ? 'Choose the recovery provider performing this trip.'
              : null;

  const handleSubmit = async () => {
    if (blockedReason) return;
    setError(null);
    setPhoneError(null);
    setSubmitting(true);
    // Keeping the leg's current provider on a reassignment sends no providerId, so the backend
    // keeps the snapshot taken at the original assignment instead of re-reading today's registry.
    // A Zofeur leg never sends one (#1254): orders assigns the Zofeur card's row.
    const keepsCurrent = currentProvider !== null && providerId === currentProvider.providerId;
    try {
      await onSubmit({
        driverName: driverName.trim() || undefined,
        driverPhone: driverPhone.trim() || undefined,
        providerId: zofeur || keepsCurrent ? undefined : providerId,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to assign driver.';
      // Spec 072 — the server's phone rule belongs under the phone field, not in the banner.
      if (message.includes(INSURANCE_MESSAGES.phoneRequired)) setPhoneError(INSURANCE_MESSAGES.phoneRequired);
      else setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxWidth: '440px', width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="admin-modal-close" onClick={onClose} disabled={submitting} aria-label="Close">&times;</button>
        </div>

        <div className="admin-modal-body">
          <div className="admin-form-group">
            {zofeur ? (
              <div className="admin-form-label" id="assign-provider-label">Provider</div>
            ) : (
              <label className="admin-form-label" htmlFor="assign-provider">Recovery provider (required)</label>
            )}
            {providers.status === 'loading' && (
              <div style={{ fontSize: '0.85rem', color: 'var(--admin-text-muted)' }} data-testid="providers-loading">
                {zofeur ? 'Loading the Zofeur settings…' : 'Loading recovery providers…'}
              </div>
            )}
            {providers.status === 'error' && (
              <div className="admin-info-banner danger" role="alert" data-testid="providers-error">
                {providers.message}{' '}
                <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={loadProviders}>
                  Try again
                </button>
              </div>
            )}
            {registryEmpty && zofeur && (
              <div className="admin-info-banner warning" role="status" data-testid="providers-empty">
                Zofeur is not set up yet. Save the Zofeur card in{' '}
                <Link to="/finance?tab=delivery">Finance → Delivery &amp; Recovery</Link> first.
              </div>
            )}
            {zofeur && zofeurRow && (
              <div
                className="admin-form-input"
                data-testid="assign-zofeur-provider"
                aria-labelledby="assign-provider-label"
                aria-readonly="true"
                style={{ background: 'var(--admin-bg)', fontWeight: 600 }}
              >
                Zofeur
              </div>
            )}
            {registryEmpty && !zofeur && (
              <div className="admin-info-banner warning" role="status" data-testid="providers-empty">
                Add {typeLabel ? `a ${typeLabel} provider` : 'a recovery provider'} in{' '}
                <Link to="/finance?tab=delivery">Finance → Delivery &amp; Recovery</Link> first.
              </div>
            )}
            {providers.status === 'loaded' && !registryEmpty && !zofeur && (
              <select
                id="assign-provider"
                className="admin-form-input"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                disabled={submitting}
                required
                aria-required="true"
              >
                <option value="">Choose a provider…</option>
                {currentProvider !== null && currentIsInactive && (
                  <option value={currentProvider.providerId}>
                    {currentProvider.providerName ?? 'Current provider'} (current, inactive — keep)
                  </option>
                )}
                {activeProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.type === 'IN_HOUSE' ? ' (own fleet)' : ''}
                    {currentProvider?.providerId === p.id ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            )}
            {zofeur ? (
              <div data-testid="assign-transport-filter" style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
                This order is <strong>{typeLabel}</strong>: Zofeur drives it. Its VAT status, cost and insurance
                come from the Zofeur card and are frozen onto this trip now.
              </div>
            ) : (
              <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
                Who performs this trip — Garagat&rsquo;s cost, never shown to the driver or the customer.
                {currentProvider !== null && ' Keeping the current provider keeps the cost frozen at the original assignment.'}
              </div>
            )}
            {typeLabel && !zofeur && (
              <div data-testid="assign-transport-filter" style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
                This order is <strong>{typeLabel}</strong>, so only {typeLabel} providers are listed.
              </div>
            )}
            {selectedId !== '' && (
              <div
                className="admin-info-banner info"
                role="status"
                data-testid="assign-disclaimer-preview"
                style={{ marginTop: '0.5rem', marginBottom: 0 }}
              >
                {preview ? (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Customer will see:</div>
                    <div>{preview}</div>
                  </>
                ) : zofeur ? 'No disclaimer shown for Zofeur' : 'No disclaimer shown for this provider'}
              </div>
            )}
          </div>

          <p style={{ color: 'var(--admin-text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {phoneRequired
              ? `${zofeur ? 'Zofeur\u2019s' : 'This provider\u2019s'} disclaimer is shown to the customer, so the driver\u2019s phone is required for the WhatsApp confirmation.`
              : 'Driver name and phone are optional — leave blank to record an unnamed assignment.'}
          </p>

          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="assign-driver-name">Driver Name</label>
            <input
              id="assign-driver-name"
              type="text"
              className="admin-form-input"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              placeholder="e.g. Ahmed Ali"
              disabled={submitting}
            />
          </div>

          <div className="admin-form-group">
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <label className="admin-form-label" htmlFor="assign-driver-phone">Driver Phone</label>
              {phoneRequired && (
                <span data-testid="assign-phone-required" aria-hidden="true" style={{ color: 'var(--admin-danger)', fontSize: '0.8125rem' }}>*</span>
              )}
            </div>
            <input
              id="assign-driver-phone"
              type="text"
              className="admin-form-input"
              value={driverPhone}
              onChange={(e) => { setDriverPhone(e.target.value); setPhoneError(null); }}
              placeholder="e.g. +20100..."
              disabled={submitting}
              required={phoneRequired}
              aria-required={phoneRequired}
              aria-invalid={!!phoneError}
              aria-describedby={phoneError ? 'assign-driver-phone-error' : undefined}
            />
            {phoneError && (
              <div id="assign-driver-phone-error" data-testid="assign-phone-error" role="alert" style={{ fontSize: '0.75rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>
                {phoneError}
              </div>
            )}
          </div>

          {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
        </div>

        <div className="admin-modal-footer">
          {blockedReason && !submitting && (
            <span
              id="assign-blocked-reason"
              data-testid="assign-blocked-reason"
              style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginRight: 'auto' }}
            >
              {blockedReason}
            </span>
          )}
          <button className="admin-btn admin-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleSubmit}
            disabled={submitting || blockedReason !== null}
            aria-describedby={blockedReason ? 'assign-blocked-reason' : undefined}
          >
            {submitting ? 'Assigning…' : 'Assign Driver'}
          </button>
        </div>
      </div>
    </div>
  );
}
